import React, { useState } from 'react';
import { AnchorIcon, FishIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
const steps = [['01', 'PICK A LURE.', 'Choose your lure.'], ['02', 'CAST.', 'Throw it into the water.'], ['03', 'CATCH.', 'Catch real products and experiences.'], ['04', 'GET IT.', 'Redeem your catch with a participating partner.']];
export function GoneFishing() {
  const reduceMotion = useReducedMotion();
  return <section id="gone-fishing" className="water-lines relative overflow-hidden bg-[#062c50] px-5 py-24 text-white md:px-10 md:py-32" aria-labelledby="fishing-heading" data-id="element-418">
      <motion.div animate={reduceMotion ? undefined : {
      x: ['-4%', '4%', '-4%']
    }} transition={{
      duration: 11,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="pointer-events-none absolute inset-x-0 top-[44%] h-16 border-y border-[#72c7e8]/20" data-id="element-419" />
      <div className="relative mx-auto max-w-[1280px]" data-id="element-420">
        <p className="text-xs font-extrabold tracking-[0.16em] text-[#72c7e8]" data-id="element-421">
          OUR FIRST CONSUMER EXPERIENCE
        </p>
        <div className="mt-5 grid gap-10 lg:grid-cols-[1fr_0.85fr] lg:items-end" data-id="element-422">
          <div data-id="element-423">
            <h2 id="fishing-heading" className="font-display text-5xl font-bold leading-[0.88] md:text-8xl" data-id="element-424">
              We filled the world’s waters with free stuff.
            </h2>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/70" data-id="element-425">
              Welcome to Gone Fishing. A mobile experience where people catch
              real products, food, drinks, experiences and rewards hidden in the
              water around them.
            </p>
            <p className="mt-9 font-display text-4xl font-bold text-[#72c7e8] md:text-6xl" data-id="element-426">
              Yes.
              <br data-id="element-427" />
              Real products.
            </p>
          </div>
          <FishingPhone data-id="element-428" />
        </div>

        <div className="relative mt-20 border-t border-[#72c7e8]/30 pt-10" data-id="element-429">
          <p className="text-xs font-extrabold tracking-[0.16em] text-[#72c7e8]" data-id="element-430">
            HOW IT WORKS
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-4" data-id="element-431">
            {steps.map(([number, title, copy]) => <article key={title} className="border border-[#72c7e8]/30 bg-[#062c50] p-5" data-id="element-432">
                <p className="text-xs font-bold text-[#72c7e8]" data-id="element-433">{number}</p>
                <h3 className="mt-10 font-display text-3xl font-bold" data-id="element-434">
                  {title}
                </h3>
                <p className="mt-3 text-sm text-white/65" data-id="element-435">{copy}</p>
              </article>)}
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-5 border-y border-[#72c7e8]/25 py-6 text-sm font-bold" data-id="element-436">
          <span data-id="element-437">10,000 chocolate bars just entered the water.</span>
          <span className="rounded-full border border-[#72c7e8]/50 px-4 py-2 text-[#72c7e8]" data-id="element-438">
            RARE CATCH DETECTED
          </span>
          <span data-id="element-439">Something big is swimming nearby.</span>
        </div>
      </div>
    </section>;
}
function FishingPhone() {
  const [demoState, setDemoState] = useState<'ready' | 'caught' | 'viewing'>('ready');
  const reduceMotion = useReducedMotion();
  const hasCatch = demoState !== 'ready';
  return <motion.div initial={reduceMotion ? false : {
    opacity: 0,
    y: 25
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="relative mx-auto w-full max-w-[320px] rounded-[42px] border-[8px] border-[#11110f] bg-[#11110f] p-2 shadow-[14px_14px_0_#72c7e8]" data-id="element-440">
      <div className="min-h-[540px] overflow-hidden rounded-[31px] bg-[#0c5c94] p-5" data-id="element-441">
        <div className="mx-auto h-5 w-20 rounded-full bg-[#11110f]" data-id="element-442" />
        <div className="mt-8 flex items-center justify-between" data-id="element-443">
          <p className="text-xs font-bold tracking-[0.14em] text-[#72c7e8]" data-id="element-444">
            GONE FISHING
          </p>
          <AnchorIcon size={17} aria-hidden="true" data-id="element-445" />
        </div>
        <p className="mt-5 font-display text-3xl font-bold leading-none" data-id="element-446">
          {demoState === 'viewing' ? 'Your catch is ready to view.' : 'Something big is swimming nearby.'}
        </p>
        <div className="relative mt-8 h-64 overflow-hidden rounded-[28px] border border-[#72c7e8]/50 water-lines" data-id="element-447">
          <motion.div animate={reduceMotion ? undefined : {
          y: [-8, 8, -8],
          x: [-4, 4, -4]
        }} transition={{
          duration: 4,
          repeat: Infinity
        }} className="absolute left-[36%] top-[36%] grid h-16 w-16 place-items-center rounded-full bg-[#ff4d17]" data-id="element-448">
            <FishIcon size={25} aria-hidden="true" data-id="element-449" />
          </motion.div>
          <motion.div animate={reduceMotion ? undefined : {
          y: [8, -8, 8]
        }} transition={{
          duration: 5,
          repeat: Infinity
        }} className="absolute right-[18%] top-[17%] h-12 w-6 rounded-full border-2 border-white bg-[#f3c64b]" data-id="element-450" />
          <div className="absolute bottom-4 left-4 rounded-full bg-white px-3 py-2 text-xs font-bold text-[#062c50]" data-id="element-451">
            2.4 KM AWAY
          </div>
        </div>

        <button type="button" onClick={() => setDemoState('caught')} aria-pressed={hasCatch} className="mt-6 w-full rounded-xl bg-[#ff4d17] py-4 text-sm font-extrabold transition-colors hover:bg-[#dd3d0f] active:bg-[#c9340b]" data-id="element-452">
          {hasCatch ? 'LURE CAST' : 'CAST LURE'}
        </button>

        <div className="mt-5 rounded-2xl border border-[#72c7e8]/30 p-4" data-id="element-453">
          <p className="text-xs font-bold text-[#72c7e8]" data-id="element-454">
            {hasCatch ? 'CONGRATS.' : 'DEMO INTERACTION'}
          </p>
          <p className="mt-1 text-sm font-bold" data-id="element-455">
            {hasCatch ? 'YOU CAUGHT A BIG MAC.' : 'CAST YOUR LURE TO SEE A DEMO CATCH.'}
          </p>
          <div className="mt-3 flex gap-2" data-id="element-456">
            <button type="button" onClick={() => setDemoState('viewing')} aria-pressed={demoState === 'viewing'} disabled={!hasCatch} className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[#062c50] transition-colors hover:bg-[#72c7e8] disabled:cursor-not-allowed disabled:opacity-45" data-id="element-457">
              VIEW CATCH
            </button>
            <button type="button" onClick={() => setDemoState('ready')} aria-pressed={demoState === 'ready'} className="rounded-full border border-white/50 px-3 py-1.5 text-xs font-bold transition-colors hover:bg-white hover:text-[#062c50]" data-id="element-458">
              KEEP FISHING
            </button>
          </div>
          <p className="mt-3 text-[10px] font-bold tracking-[0.08em] text-white/65" aria-live="polite" data-id="element-459">
            {demoState === 'ready' ? 'ILLUSTRATIVE CONSUMER EXPERIENCE' : demoState === 'caught' ? 'DEMO CATCH ADDED' : 'DEMO CATCH OPENED'}
          </p>
        </div>
      </div>
    </motion.div>;
}